import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import useAuth from 'src/hooks/useAuth';
import useConfirm from 'src/hooks/useConfirm';
import useViewMessage from 'src/hooks/useViewMessage';
import { Message } from 'src/services/messages_services/useGetMessageApi';
import useUndeleteMessagesApi from 'src/services/messages_services/useUnDeleteMessagesApi';
import MessageActionPopUpContentWrapper from './MessageActionPopUpContentWrapper';
import useMessageButtonState from './useMessageButtonState';

interface UndeleteMessageProps {
  clientId?: string;
  selectedUndel: Message[];
  selectedDel: Message[];
  origin?: string;
}

const UndeleteMessage: FC<UndeleteMessageProps> = ({
  clientId,
  selectedDel,
  selectedUndel,
  origin = 'outbox',
}) => {
  const { confirm } = useConfirm();
  const { refreshTable } = useViewMessage();
  const { t } = useTranslation();
  const { clientId: authClientId } = useAuth();

  //determine if deleting or undeleting and set values accordingly
  let messageIds: number[] = [];
  let isDelete = false;
  let messages: any[] = [];

  if (selectedDel.length > 0) {
    isDelete = true;
    messageIds = selectedDel.map((msg) => msg.messageId);
    messages = selectedDel;
  } else if (selectedUndel.length > 0) {
    isDelete = false;
    messageIds = selectedUndel.map((msg) => msg.messageId);
    messages = selectedUndel;
  }

  const { undeleteMessage } = useUndeleteMessagesApi(
    clientId ?? '',
    messageIds,
    isDelete,
    origin,
    messages
  );
  const [message, buttonState] = useMessageButtonState('Undelete');
  const CountUndel = selectedUndel.length;
  const CountDel = selectedDel.length;

  const handleUndeleteMessage = async () => {
    const isUndeleteApproved = await confirm({
      title: t(`Undelete Message(s)`),
      content: t(`Undelete selected message(s)?`),
      confirmButtonText: t('OK'),
    });

    if (!isUndeleteApproved) {
      return;
    }

    const messageIds = selectedUndel.map((msg) => msg.messageId); //messageIds to UNdelete

    if (!clientId || String(clientId).trim() === '') {
      console.error('clientId required to undelete message(s)');
      toast.error(t('Unable to undelete: No valid client ID'));
      return;
    }

    if (!messageIds || messageIds === undefined || messageIds.length === 0) {
      console.error('messageId(s) required to undelete message(s)');
      toast.error(t('Unable to undelete: No messages selected'));
      return;
    }

    // Enrich message objects with recipientClientId (use authClientId for signed-on user)
    const recipientClientId = authClientId || clientId;
    const enrichedMessages = selectedUndel.map((msg) => ({
      ...msg,
      clientId: recipientClientId, // This will be used for RecipientClientId in the API
    }));

    try {
      const result = await undeleteMessage({
        clientId,
        messageIds,
        isDeleteAction: false,
        messages: enrichedMessages,
        origin,
      }); //false means UNdelete

      toast.success(t('Message(s) successfully undeleted'));

      refreshTable(); // Refresh from server to get updated data
    } catch (error) {
      toast.error(t('Error undeleting message(s)'));
      console.error(error);
    }
  };

  if (CountUndel === 0 && CountDel === 0) {
    return (
      <PopUp
        popupAlign={{ horizontal: 'center', vertical: 'top' }}
        content={
          <div className="m-1 max-w-[385px]" style={{ fontSize: '13.5px' }}>
            <MessageActionPopUpContentWrapper
              type="Information"
              contents={[t('Please select one or more records to Undelete.')]}
            />
          </div>
        }
        show={true}
      >
        <Button disabled={true} color="dark">
          {t('Undelete')}
        </Button>
      </PopUp>
    );
  }

  if (CountDel > 0) {
    return (
      <PopUp
        popupAlign={{ horizontal: 'center', vertical: 'top' }}
        content={
          <div className="m-1 max-w-[385px]" style={{ fontSize: '13.5px' }}>
            <MessageActionPopUpContentWrapper
              type="Error"
              contents={selectedDel.map(
                (msg) =>
                  `${t('Message Id')} ${String(msg.messageId)} ${t('is not eligible to undelete')}`
              )}
            />
          </div>
        }
        show={true}
      >
        <Button disabled={true} color="dark">
          {t('Undelete')}
        </Button>
      </PopUp>
    );
  } else {
    return (
      <Button disabled={CountUndel === 0} color="error" onClick={handleUndeleteMessage}>
        {t('Undelete')}
      </Button>
    );
  }
};

export default UndeleteMessage;
